/* eslint-disable no-undef */
import React, { useEffect, useState } from 'react';
import Layout from 'antd/lib/layout';
import Menu from 'antd/lib/menu';
import PageHeader from 'antd/lib/page-header';
import Input from 'antd/lib/input';
import Button from 'antd/lib/button';
import { Loader } from '@googlemaps/js-api-loader';
import { getBuildingList, getAlertsCount } from 'parity-network-requests';
import { HeaderComponent } from '../Header';
import { MultiBuildingModal } from '../MultiBuildingModal';
import { AddBuildingModal } from './components/AddBuildingModal';
import { config } from '../../config';

import './MultiBuildingHome.less';

// ICONS
import buildingLogoLight from '../../icons/building-icon-light.svg';
import buildingLogoDark from '../../icons/building-icon-dark.svg';
import buildingLogoSelectedLight from '../../icons/building-icon-selected-light.svg';
import buildingLogoSelectedDark from '../../icons/building-icon-selected-dark.svg';
import mapMarker from './assets/map-marker.svg';
import mapMarkerRed from './assets/map-marker-red.svg';
import addGrey from '../../icons/add-grey.svg';
import addWhite from '../../icons/add-white.svg';
import addDark from '../../icons/add-dark.svg';
import addLightGrey from '../../icons/add-light-grey.svg';
import searchIcon from '../../icons/search.svg';
import searchDarkIcon from '../../icons/search-dark.svg';

// IMAGES
import addBuildingsLogo from '../../images/adding-buildings.png';

const mainStyle = {
  height: '100%',
};

const { REACT_APP_BUILD_ENV } = config;

export const MultiBuildingHome = (props) => {
  const { Sider, Content } = Layout;
  const { updateBuildings, theme, changeTheme } = props;
  const { Search } = Input;
  let timeout = 0;
  const [buildings, setBuildings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchedBuildings, setSearchedBuildings] = useState([]);
  const [buildingAlerts, setBuildingAlerts] = useState([]);
  const [isAddBuildingModalOpen, setIsAddBuildingModalOpen] = useState(false);
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [selectedBuildingInfo, setSelectedBuildingInfo] = useState({});
  const themedSearch = theme === 'dark' ? searchDarkIcon : searchIcon;

  /* eslint-disable no-param-reassign */
  const mapAlertsToBuildings = (buildingsList, buildingAlertsList) => {
    buildingsList.forEach((building) => {
      buildingAlertsList.forEach((buildingAlert) => {
        if (building.identifier === buildingAlert.building_identifier) {
          building.alerts = buildingAlert.alert_count;
        }
      });
    });
    buildingsList.sort((a, b) => b.alerts - a.alerts);
  };

  useEffect(() => {
    async function fetchData() {
      const buildingsList = await getBuildingList(REACT_APP_BUILD_ENV);
      updateBuildings(buildingsList);
      const buildingAlertsList = await getAlertsCount(REACT_APP_BUILD_ENV);
      mapAlertsToBuildings(buildingsList, buildingAlertsList);
      setBuildings(buildingsList);
      setSearchedBuildings(buildingsList);
      setBuildingAlerts(buildingAlertsList);
    }

    if (!isAddBuildingModalOpen) {
      fetchData();
    }

    window.document.title = `Home`;

    sessionStorage.removeItem('selectedBuilding');

    setIsLoading(false);
  }, [isAddBuildingModalOpen]);

  const getSearchedBuildingList = async (search) => {
    const buildingsList = await getBuildingList(REACT_APP_BUILD_ENV, search);
    mapAlertsToBuildings(buildingsList, buildingAlerts);
    setSearchedBuildings(buildingsList);
  };

  const closeAddBuildingModal = async () => {
    setIsAddBuildingModalOpen(false);
  };

  const selectBuilding = (building) => {
    const { name, identifier, city, country, province, postal_code: postalCode, latitude, longitude } = building;

    setSelectedBuildingInfo({
      name,
      city,
      country,
      province,
      postalCode,
      latitude,
      longitude,
    });
    setSelectedBuilding(identifier);
  };

  const getBuildingIcon = (identifier) => {
    if (selectedBuilding === identifier) {
      return theme === 'light' ? (
        <img src={buildingLogoSelectedLight} alt="selected building logo" style={{ marginRight: '1rem' }} />
      ) : (
        <img src={buildingLogoSelectedDark} alt="selected building logo" style={{ marginRight: '1rem' }} />
      );
    }
    return theme === 'light' ? (
      <img src={buildingLogoLight} alt="building logo" style={{ marginRight: '1rem' }} />
    ) : (
      <img src={buildingLogoDark} alt="building logo" style={{ marginRight: '1rem' }} />
    );
  };

  const loader = new Loader({
    apiKey: 'AIzaSyBmqYJF_Ej9xwD_MRzpWfDSnwW_vHy3pnw',
    version: 'weekly',
  });
  loader.load().then(() => {
    const { latitude: selectedBuildingLatitude, longitude: selectedBuildingLongitude } = selectedBuildingInfo;

    const map = new google.maps.Map(document.getElementById('main-map'), {
      fullscreenControl: false,
      streetViewControl: false,
      mapTypeControl: false,
      clickableIcons: false,
      styles: [
        {
          featureType: 'all',
          elementType: 'geometry.fill',
          stylers: [
            {
              color: theme === 'light' ? '#f5f7f6' : '#444546',
              visibility: 'on',
            },
          ],
        },
        {
          featureType: 'all',
          elementType: 'geometry.stroke',
          stylers: [
            {
              visibility: theme === 'light' ? 'inherit' : 'on',
              color: theme === 'light' ? 'unset' : '#61666b',
              weight: theme === 'light' ? 'unset' : 1,
            },
          ],
        },
        {
          featureType: 'all',
          elementType: 'labels.text.fill',
          stylers: [
            {
              color: theme === 'light' ? '#a4a3a3' : '#b9c1c8',
            },
          ],
        },
        {
          featureType: 'all',
          elementType: 'labels.text.stroke',
          stylers: [
            {
              color: theme === 'light' ? 'unset' : '#333333',
            },
          ],
        },
        {
          featureType: 'administrative.locality',
          elementType: 'geometry.fill',
          stylers: [
            {
              visibility: 'off',
            },
          ],
        },
        {
          featureType: 'administrative.province',
          elementType: 'geometry.fill',
          stylers: [
            {
              visibility: 'on',
            },
          ],
        },
        {
          featureType: 'administrative.neighborhood',
          elementType: 'geometry.fill',
          stylers: [
            {
              visibility: 'on',
            },
          ],
        },

        {
          featureType: 'water',
          elementType: 'geometry.fill',
          stylers: [
            {
              color: theme === 'light' ? '#d1e7f6' : '#181726',
            },
          ],
        },
        {
          featureType: 'water',
          elementType: 'labels.text',
          stylers: [
            {
              color: theme === 'light' ? '#a4a3a3' : '#246f8f',
            },
          ],
        },
        {
          featureType: 'road',
          elementType: 'all',
          stylers: [
            {
              color: theme === 'light' ? '#ffffff' : '#3c3c3c',
              visibility: 'on',
            },
          ],
        },
        {
          featureType: 'road',
          elementType: 'labels.text.fill',
          stylers: [
            {
              color: '#a4a3a3',
            },
          ],
        },
        {
          featureType: 'road.highway',
          elementType: 'all',
          stylers: [
            {
              visibility: 'off',
            },
          ],
        },
        {
          featureType: 'poi',
          elementType: 'all',
          stylers: [
            {
              visibility: 'off',
            },
          ],
        },
        {
          featureType: 'transit',
          elementType: 'all',
          stylers: [
            {
              visibility: 'off',
            },
          ],
        },
      ],
    });

    const bounds = new google.maps.LatLngBounds();

    if (buildings.length > 0) {
      buildings.map((building) => {
        const { latitude, longitude } = building;

        const marker = new google.maps.Marker({
          position: { lat: parseFloat(latitude), lng: parseFloat(longitude) },
          icon: building.alerts ? mapMarkerRed : mapMarker,
          map,
        });

        bounds.extend(marker.position);

        marker.addListener('click', () => selectBuilding(building));

        return marker;
      });
    } else {
      bounds.extend({ lat: 43.6464503, lng: -79.4049989 });
    }

    // Center on the selected building or include all the buildings on the map
    if (selectedBuilding) {
      // Adjusting latitude by 0.05 so the building modal does not cover the marker
      map.setCenter({ lat: parseFloat(selectedBuildingLatitude), lng: parseFloat(selectedBuildingLongitude) + 0.05 });
      // Zoom out after the map is centered
      const listener = google.maps.event.addListener(map, 'idle', () => {
        map.setZoom(13);
        google.maps.event.removeListener(listener);
      });
    } else {
      map.fitBounds(bounds);

      if (buildings.length <= 1) {
        // Zoom out after the map is done scaling
        const listener = google.maps.event.addListener(map, 'idle', () => {
          map.setZoom(13);
          google.maps.event.removeListener(listener);
        });
      }
    }
  });

  return (
    <Layout className={`ant-layout-has-sider-${theme}`} style={mainStyle}>
      {!isLoading && (
        <Sider
          width={300}
          className="main-sider"
          style={{ justifyContent: 'flex-start', height: '100%', overflow: 'auto' }}
          theme={theme}
        >
          <PageHeader title={`My Buildings (${buildings.length})`} />
          {buildings.length > 0 ? (
            <>
              <div className="main-sider__search">
                <Search
                  id="search"
                  placeholder="Search buildings"
                  allowClear
                  prefix={<img src={themedSearch} alt="search icon" />}
                  style={{ width: '80%' }}
                  enterButton={true}
                  bordered={false}
                  onChange={(e) => {
                    e.persist();
                    if (timeout) clearTimeout(timeout);
                    timeout = setTimeout(() => {
                      getSearchedBuildingList(e.target.value);
                    }, 500);
                  }}
                  onSearch={(value) => {
                    getSearchedBuildingList(value);
                  }}
                />
              </div>
              <div className="main-sider__add-building-container">
                <div
                  className="main-sider__add-building"
                  onClick={() => setIsAddBuildingModalOpen(true)}
                  onKeyDown={() => setIsAddBuildingModalOpen(true)}
                  role="button"
                  tabIndex="0"
                >
                  {theme === 'light' ? (
                    <img src={addGrey} alt="add button logo" style={{ marginRight: '1rem' }} />
                  ) : (
                    <img src={addLightGrey} alt="add button logo" style={{ marginRight: '1rem' }} />
                  )}
                  <span>Add Buildings</span>
                </div>
              </div>
              <Menu mode="inline" theme={theme} selectedKeys={[selectedBuilding]}>
                {searchedBuildings.map((building) => {
                  const { alerts, identifier, name } = building;
                  return (
                    <Menu.Item key={identifier} onClick={() => selectBuilding(building)}>
                      {getBuildingIcon(identifier)}
                      <span>{name}</span>
                      {alerts ? <div className="main-sider__building-alerts-count">{alerts}</div> : null}
                    </Menu.Item>
                  );
                })}
              </Menu>
            </>
          ) : (
            <div className="no-buildings">
              <img src={addBuildingsLogo} alt="add buildings" className="add-buildings-logo" />
              <Button
                block
                htmlType="submit"
                size="large"
                className="add-buildings__button"
                onClick={() => setIsAddBuildingModalOpen(true)}
              >
                <div className="add-buildings__button-text">
                  {theme === 'light' ? (
                    <img className="add-building-icon" src={addWhite} alt="add button logo" />
                  ) : (
                    <img className="add-building-icon" src={addDark} alt="add button logo" />
                  )}
                  Add Buildings
                </div>
              </Button>
              <div className="no-buildings-text">Your buildings will be shown here once you&apos;ve added them</div>
            </div>
          )}
        </Sider>
      )}
      {isAddBuildingModalOpen && <AddBuildingModal onCancel={closeAddBuildingModal} theme={theme} />}
      <Content style={{ height: '100vh' }}>
        <HeaderComponent
          buildings={buildings}
          theme={theme}
          changeTheme={changeTheme}
          addBuilding={() => setIsAddBuildingModalOpen(true)}
        />
        <div id="main-map" className="main-map" />
      </Content>
      {selectedBuilding && (
        <MultiBuildingModal
          buildingInfo={selectedBuildingInfo}
          building={selectedBuilding}
          onCancel={() => {
            setSelectedBuilding('');
            setSelectedBuildingInfo({});
          }}
          theme={theme}
          buildings={buildings}
        />
      )}
    </Layout>
  );
};
